import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Multiselect from "@cloudscape-design/components/multiselect";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Toggle from "@cloudscape-design/components/toggle";
import Spinner from "@cloudscape-design/components/spinner";
import RangePicker from "@cloudscape-design/components/date-range-picker";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore } from "../stores/config";
import { gameStore } from "../stores/games";
import { gamesEditModalStore } from "../stores/gamesEditModal";
import { gameSectionStore } from "../stores/gamesSection";
import { gamesTagsStore } from "../stores/gamesTagsModal";
/* ------- */

const BADGE_POSITION_OPTIONS = [
	{ label: "Top left", value: "top_left" },
	{ label: "Top right", value: "top_right" },
	{ label: "Bottom left", value: "bottom_left" },
	{ label: "Bottom right", value: "bottom_right" },
];
const SYSTEM_RTP_BADGE_VALUE = "__system_rtp_up__";

class GamesEditModal extends React.Component {
	constructor(props) {
		super(props);
		this.searchDebounce = null;

		this.state = {
			id: null,
			isTagsLoading: false,
			oldTags: [],
		};
	}
	componentDidUpdate(prevProps) {
		if (prevProps.gamesEditModal.id !== this.props.gamesEditModal.id && this.props.gamesEditModal.id) {
			this.setState({ id: this.props.gamesEditModal.id });
			setTimeout(() => {
				this.loadGameParams();
			}, 100);
		}

		if (this.props.gamesEditModal.tagIds !== prevProps.gamesEditModal.tagIds) {
			this.setState({ isTagsLoading: true, oldTags: this.props.gamesEditModal.tagIds });
			this.props.Interface.loadGameTagsByIds(this.props.gamesEditModal.tagIds)
				.then((tags) => {
					this.props.setGamesEditModal((prev) => ({ ...prev, tags }));
				})
				.finally(() => {
					this.setState({ isTagsLoading: false });
				});
		}
	}

	componentDidMount() {
		this.loadGamesSection();
		this.loadBadges();
		this.loadGameParams();

		this.props.setGameTags((prev) => ({
			...prev,
			filters: {
				...prev.filters,
				sorting: {
					column: "created_at",
					order: "desc",
				},
				search: "",
				page: 1,
				limit: 100,
			},
		}));
		setTimeout(() => {
			this.props.Interface.loadGameTags();
		}, 50);
	}

	componentWillUnmount() {
		if (this.searchDebounce) clearTimeout(this.searchDebounce);

		this.setState({ isTagsLoading: false, oldTags: [] });

		this.props.setGameTags((prev) => ({
			...prev,
			filters: {
				...prev.filters,
				sorting: {
					column: "created_at",
					order: "desc",
				},
				search: "",
				page: 1,
				limit: 5,
			},
		}));

		this.props.setGamesEditModal((prev) => ({
			...prev,
			tags: [],
			tagIds: [],
		}));

		this.props.Interface.abortAllRequests();
	}

	loadGamesSection() {
		this.props.Interface.request(
			"get",
			{
				class: "data",
				module: "gamesSection",
				method: "list",
			},
			{ name: "test" },
		)
			.then((list) => {
				const options = list.map((section) => ({
					value: String(section.id),
					label: section.name,
				}));
				this.props.setGamesEditModal((prev) => ({
					...prev,
					currentOption: { ...prev.currentOption, section: options },
				}));
			})
			.catch((error) => {
				console.log(error);
				//	if (typeof error.text === "undefined") error = { text: error.toString() };
				//§	this.props.setGameSection({ ...this.props.gameSection, loaded: true, error });
			});
	}

	loadBadges() {
		this.props.Interface.request(
			"get",
			{
				class: "data",
				module: "badges",
				method: "list",
			},
			{ name: "test" },
		)
			.then((list) => {
				const customOptions = (list || [])
					.filter((badge) => String(badge?.name || "").toLowerCase() !== "rtpup")
					.map((badge) => ({
						value: String(badge.id),
						label: badge.name,
						status: String(badge.status || "").toUpperCase(),
						textColor: badge.text_color,
						backgroundColor: badge.background_color,
					}));
				const options = [
					{
						value: SYSTEM_RTP_BADGE_VALUE,
						label: "RTP Up",
						status: "ACTIVE",
						systemType: "rtpUp",
					},
					...customOptions,
				];
				this.props.setGamesEditModal((prev) => ({
					...prev,
					currentOption: { ...prev.currentOption, badges: options },
				}));
			})
			.catch((error) => {
				console.log(error);
			});
	}

	loadGameParams() {
		this.props.setGamesEditModal({ ...this.props.gamesEditModal, section: [], badges: [], loaded: false, badgeAssignments: [] });
		this.setState({ id: this.props.gamesEditModal.id });
		this.props.Interface.request("get", { class: "data", module: "games", method: "params" }, { gameID: this.props.gamesEditModal.id, edit: true })
			.then((params) => {
				let gameParams = cloneDeep(this.props.gamesEditModal),
					keyParams = [],
					categoryID = [];

				Object.keys(params).forEach((key) => {
					if (key !== "categoryID") {
						if (key === "biases") {
							gameParams[key] = { ...params[key], from: params[key].from ? params[key].from.split("T")[0] : "", to: params[key].to ? params[key].to.split("T")[0] : "" };
						} else if (key === "tags" || key === "metaTags") {
							gameParams.tagIds =
								params["tags"]
									?.split(",")
									.filter(Boolean)
									.map((tag) => tag.trim()) || [];
						} else {
							gameParams[key] = params[key];
						}

						if (key === "sectionIDs") {
							const rawSection = params[key];
							if (Array.isArray(rawSection)) {
								gameParams.section = rawSection.map((value) => parseInt(value)).filter((value) => !isNaN(value));
							} else if (!isNaN(parseInt(rawSection))) {
								gameParams.section = [parseInt(rawSection)];
							} else {
								gameParams.section = [];
							}
						}
						if (key === "badges") {
							const normalizedBadges = Array.isArray(params[key]) ? params[key] : [];
							gameParams.badges = normalizedBadges.map((item) => parseInt(item.badgeId, 10)).filter((id) => !isNaN(id) && id > 0);
							gameParams.badgeAssignments = normalizedBadges
								.map((item) => ({
									badgeId: String(item?.name || "").toLowerCase() === "rtpup" ? SYSTEM_RTP_BADGE_VALUE : parseInt(item.badgeId, 10),
									systemType: String(item?.name || "").toLowerCase() === "rtpup" ? "rtpUp" : undefined,
									position: item.position,
									rtpValue: item.rtpValue ?? item.rtp_value ?? "",
								}))
								.filter((item) => (item.systemType === "rtpUp" ? Boolean(item.position) : !isNaN(item.badgeId) && item.badgeId > 0));
						}

						if (key !== "sectionIDs" && key !== "badges") keyParams.push(key);
					} else
						this.props.config.game.options.category.slice(1).forEach((category) => {
							params[key].forEach((paramCategory) => {
								if (category.type === parseInt(paramCategory)) categoryID.push(category);
							});
						});
				});

				delete gameParams.sectionIDs;
				this.props.setGamesEditModal({ ...this.props.gamesEditModal, ...gameParams, loaded: true, params: keyParams, categoryID, newPoster: false, newBigPoster: false });
			})
			.catch((e) => {
				this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: true });
				console.log(e);
			});
	}

	editAllGames = async () => {
		const { setGamesEditModal, gamesEditModal, Interface, game, setGame } = this.props;
		setGamesEditModal({ ...gamesEditModal, loaded: false });

		try {
			const updates = game.selectedItems.map(({ gameID }) => {
				const current = game.data.find((item) => item.gameID === gameID) || {};
				return {
					...current,
					id: gameID,
					active: gamesEditModal.active ?? current.active,
					categoryID: gamesEditModal.categoryID.map(({ type }) => type) ?? current.categoryID,
				};
			});

			const res = await Interface.request("set", { class: "data", module: "games", method: "bulkEdit" }, { updates });

			updates.forEach((g) => Interface.updateGameData(g.id, g));
			Interface.addNoty("Success", "Games were successfully edited");
			setGamesEditModal({ ...gamesEditModal, loaded: true });
			setTimeout(() => {
				setGamesEditModal({ ...gamesEditModal, show: false });
				setGame({ ...game, selectedItems: [] });
			}, 0);
		} catch (error) {
			Interface.addNoty("Error", error.text || error.toString());
			setGamesEditModal({
				...gamesEditModal,
				loaded: true,
				error: {
					text: error.text || error.toString(),
					code: error.code || 500,
					timestamp: Date.now(),
				},
			});
		}
	};

	editGame() {
		this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: false });

		let params = {};
		Object.keys(this.props.gamesEditModal).forEach((key) => {
			if (key === "categoryID") {
				params[key] = [];
				this.props.gamesEditModal[key].forEach((category) => {
					params[key].push(category.type);
				});
			} else if (key !== "show" && key !== "loaded" && key !== "biases" && key !== "sectionIDs") params[key] = this.props.gamesEditModal[key];
			else if (key === "biases") {
				params.bias = this.props.gamesEditModal.biases.bias;

				if (this.props.gamesEditModal.biases.from) params.biasDateFrom = this.props.gamesEditModal.biases.from + "T00:00:00";
				if (this.props.gamesEditModal.biases.to) params.biasDateTo = this.props.gamesEditModal.biases.to + "T00:00:00";
			}

			if (key === "section") {
				params.sectionIDs = Array.isArray(this.props.gamesEditModal.section)
					? this.props.gamesEditModal.section
					: [];
			}
			if (key === "badgeAssignments") {
				params.badges = (this.props.gamesEditModal.badgeAssignments || [])
					.map((item) => {
						const isSystemRtp = item?.badgeId === SYSTEM_RTP_BADGE_VALUE || item?.systemType === "rtpUp";
						const parsedRtpValue = parseInt(item?.rtpValue, 10);
						if (isSystemRtp) {
							return {
								systemType: "rtpUp",
								position: item.position,
								rtpValue: Number.isFinite(parsedRtpValue) && parsedRtpValue > 0 ? parsedRtpValue : null,
							};
						}
						return {
							badgeId: parseInt(item.badgeId, 10),
							position: item.position,
						};
					})
					.filter((item) => (item.systemType === "rtpUp" ? item.position : !isNaN(item.badgeId) && item.badgeId > 0 && item.position));
			}

			if (key === "tags") {
				const metaTagIds = this.props.gamesEditModal.tags.map((tag) => tag.metaTagId);
				params.metaTags = [...new Set(metaTagIds)];
				params.tags = this.props.gamesEditModal.tags.map((tag) => tag.id);
			}
		});

		params.id = this.props.gamesEditModal.editID;
		params.newId = this.state.id;
		const hasDuplicatePositions = new Set((params.badges || []).map((item) => item.position)).size !== (params.badges || []).length;
		if (hasDuplicatePositions) {
			this.props.Interface.addNoty("Error", "Badge positions must be unique");
			this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: true });
			return;
		}
		const hasInvalidRtpBadge = (params.badges || []).some((item) => item.systemType === "rtpUp" && (!Number.isFinite(parseInt(item.rtpValue, 10)) || parseInt(item.rtpValue, 10) <= 0));
		if (hasInvalidRtpBadge) {
			this.props.Interface.addNoty("Error", "RTP Up badge requires RTP value");
			this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: true });
			return;
		}

		if ((this.state.oldTags || []).join(",") !== params.tags?.join(",")) {
			params.tagsChanged = true;
		}

		this.props.Interface.request("get", { class: "data", module: "games", method: "edit" }, { ...params })
			.then(() => {
				this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: true });
				setTimeout(() => {
					this.props.setGamesEditModal({ ...this.props.gamesEditModal, show: false });
				}, 0);

				//setTimeout(() => { this.props.Interface.loadCasinoData() }, 500);
				this.props.Interface.updateGameData(params.id, params);
			})
			.catch((e) => {
				this.props.setGamesEditModal({ ...this.props.gamesEditModal, loaded: true });
				console.log(e);
			});
	}

	getBase64(file, key) {
		let reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			this.updateParam(key, reader.result);
		};
	}

	updateParam(key, value) {
		let params = cloneDeep(this.props.gamesEditModal);
		params[key] = value;
		this.props.setGamesEditModal({ ...params });
	}

	render() {
		const badgeAssignments = (this.props.gamesEditModal.badgeAssignments || []).filter((assignment) => assignment && assignment.position);
		const selectedBadgeByPosition = new Map();
		const rtpValueByPosition = new Map();
		const isDarkMode = typeof document !== "undefined" && document.body.classList.contains("awsui-dark-mode");
		badgeAssignments.forEach((assignment) => {
			const isSystemRtp = assignment?.badgeId === SYSTEM_RTP_BADGE_VALUE || assignment?.systemType === "rtpUp";
			if (isSystemRtp) {
				selectedBadgeByPosition.set(assignment.position, SYSTEM_RTP_BADGE_VALUE);
				rtpValueByPosition.set(assignment.position, assignment?.rtpValue || "");
				return;
			}
			const badgeId = parseInt(assignment.badgeId, 10);
			if (!isNaN(badgeId) && badgeId > 0) selectedBadgeByPosition.set(assignment.position, String(badgeId));
		});

		return (
			<>
				<Modal
					visible={this.props.gamesEditModal.show}
					onDismiss={() => {
						this.props.setGamesEditModal({ ...this.props.gamesEditModal, show: false });
						this.setState({ id: null });
					}}
					header="Edit game"
					children={
						<>
							<SpaceBetween direction="vertical" size="m">
								{this.props.gamesEditModal.loaded ? (
									<>
										<Multiselect
											filteringType="auto"
											selectedOptions={this.props.gamesEditModal.categoryID}
											onChange={(select) => {
												this.props.setGamesEditModal({ ...this.props.gamesEditModal, categoryID: select.detail.selectedOptions });
											}}
											disabled={!this.props.game.loaded}
											placeholder="Category"
											options={this.props.config.game.options.category.slice(1)}
										/>

										<Box variant="span">current id:</Box>
										<Input
											onChange={(input) => {
												this.setState({
													id: input.detail.value,
												});
											}}
											type="number"
											placeholder="currentId"
											value={this.state.id? this.state.id: null}
										/>

										{this.props.gamesEditModal.params
											.filter((item) => {
												return item === "volatility" || item === "rtp" || item === "metaTags" || item === "name" || item === "gameID";
											})
											.map((item) => (
												<>
													<Box variant="span">{item}: </Box>
													<Input
														disableBrowserAutocorrect={true}
														placeholder={item}
														value={this.props.gamesEditModal[item]}
														onChange={(input) => {
															this.updateParam(item, input.detail.value);
														}}
													/>
												</>
											))}

										{
											<>
												<Box variant="span">current poster:</Box>
												<Input disableBrowserAutocorrect={true} placeholder="poster" value={this.props.gamesEditModal.poster} disabled="true" />
												<Box variant="span">current big poster:</Box>
												<Input disableBrowserAutocorrect={true} placeholder="poster" value={this.props.gamesEditModal.bigPoster} disabled="true" />
												<Box variant="span">poster:</Box>
												<input
													type="file"
													onChange={(ev) => {
														this.getBase64(ev.target.files[0], "newPoster");
													}}></input>
												<Box variant="span">big poster:</Box>
												<input
													type="file"
													onChange={(ev) => {
														this.getBase64(ev.target.files[0], "newBigPoster");
													}}></input>

												<SpaceBetween direction="vertical" size="m">
													<Box variant="span">Section:</Box>
													<Multiselect
														filteringType="auto"
														disabled={!this.props.game.loaded}
														placeholder="Section"
														selectedOptions={this.props.gamesEditModal.currentOption.section.filter((option) =>
															(this.props.gamesEditModal.section || []).includes(parseInt(option.value))
														)}
														onChange={(option) => {
															this.props.setGamesEditModal({
																...this.props.gamesEditModal,
																section: option.detail.selectedOptions
																	.map((selected) => parseInt(selected.value))
																	.filter((value) => !isNaN(value)),
															});
														}}
														options={this.props.gamesEditModal.currentOption.section}
													/>
												</SpaceBetween>
												<SpaceBetween direction="vertical" size="m">
													<Box variant="span">Badges:</Box>
													<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
														{BADGE_POSITION_OPTIONS.map((position) => {
															const selectedBadgeId = selectedBadgeByPosition.get(position.value);
															const selectedBadge = this.props.gamesEditModal.currentOption.badges.find((item) => String(item.value) === String(selectedBadgeId));
															const selectedIsRtpBadge = String(selectedBadgeId) === SYSTEM_RTP_BADGE_VALUE;
															const borderColor = selectedBadge ? (selectedBadge.status === "INACTIVE" ? "#d13212" : "#037f0c") : isDarkMode ? "#879596" : "#d5dbdb";
															const badgeOptions = [
																{ value: "", label: "Empty" },
																...this.props.gamesEditModal.currentOption.badges.map((item) => {
																	const itemId = String(item.value);
																	const usedInAnotherPosition = [...selectedBadgeByPosition.entries()].some(
																		([positionValue, badgeId]) => positionValue !== position.value && String(badgeId) === itemId
																	);
																	return {
																		...item,
																		label: `${item.label}${item.status === "INACTIVE" ? " (inactive)" : ""}`,
																		disabled: usedInAnotherPosition,
																	};
																}),
															];
															return (
															<div key={position.value} style={{ border: `2px solid ${borderColor}`, borderRadius: 6, padding: 8, minHeight: 80 }}>
																<div>{position.label}</div>
																<div style={selectedIsRtpBadge ? { display: "grid", gridTemplateColumns: "1fr 150px", gap: 8, alignItems: "center" } : undefined}>
																	<Select
																		placeholder="Select badge"
																		selectedOption={badgeOptions.find((item) => String(item.value) === String(selectedBadgeId)) || badgeOptions[0]}
																		options={badgeOptions}
																		onChange={(event) => {
																			const nextBadgeId = event.detail.selectedOption?.value;
																			const nextByPosition = new Map(selectedBadgeByPosition);
																			if (!nextBadgeId) nextByPosition.delete(position.value);
																			else nextByPosition.set(position.value, nextBadgeId);
																			const nextAssignments = BADGE_POSITION_OPTIONS
																				.map((item) => {
																					const badgeId = nextByPosition.get(item.value);
																					if (!badgeId) return null;
																					if (String(badgeId) === SYSTEM_RTP_BADGE_VALUE) {
																						return {
																							badgeId: SYSTEM_RTP_BADGE_VALUE,
																							systemType: "rtpUp",
																							position: item.value,
																							rtpValue: rtpValueByPosition.get(item.value) || "",
																						};
																					}
																					const parsedBadgeId = parseInt(badgeId, 10);
																					return !isNaN(parsedBadgeId) && parsedBadgeId > 0 ? { badgeId: parsedBadgeId, position: item.value } : null;
																				})
																				.filter(Boolean);
																			this.props.setGamesEditModal({
																				...this.props.gamesEditModal,
																				badges: nextAssignments.map((item) => item.badgeId),
																				badgeAssignments: nextAssignments,
																			});
																		}}
																	/>
																	{selectedIsRtpBadge ? (
																		<Input
																			type="number"
																			inputMode="number"
																			placeholder="RTP value"
																			value={String(rtpValueByPosition.get(position.value) || "")}
																			onChange={(event) => {
																				const nextValue = event.detail.value;
																				const nextAssignments = badgeAssignments.map((assignment) => {
																					if (assignment.position !== position.value) return assignment;
																					if (assignment.badgeId === SYSTEM_RTP_BADGE_VALUE || assignment.systemType === "rtpUp") return { ...assignment, badgeId: SYSTEM_RTP_BADGE_VALUE, systemType: "rtpUp", rtpValue: nextValue };
																					return assignment;
																				});
																				this.props.setGamesEditModal({
																					...this.props.gamesEditModal,
																					badgeAssignments: nextAssignments,
																				});
																			}}
																		/>
																	) : null}
																</div>
																<div style={{ marginTop: 6, minHeight: 24, color: selectedBadge ? (selectedBadge.status === "INACTIVE" ? "#d13212" : "#037f0c") : "transparent" }}>
																	{selectedBadge ? selectedBadge.status : "STATUS"}
																</div>
															</div>
															);
														})}
													</div>
												</SpaceBetween>
											</>
										}

										<SpaceBetween direction="horizontal" size="m">
											{this.props.gamesEditModal.params
												.filter((item) => {
													return item === "active";
												})
												.map((item) => (
													<>
														<Toggle
															checked={this.props.gamesEditModal[item]}
															onChange={(checkbox) => {
																this.updateParam(item, checkbox.detail.checked);
															}}>
															{item}
														</Toggle>
													</>
												))}

										</SpaceBetween>

										{
											<>
												<SpaceBetween direction="vertical" size="m">
													<Box variant="span">bias:</Box>
													<Input
														inputMode={"number"}
														type="number"
														disableBrowserAutocorrect={true}
														placeholder="bias"
														value={this.props.gamesEditModal.biases.bias}
														onChange={(input) => {
															this.updateParam("biases", { ...this.props.gamesEditModal.biases, bias: input.detail.value });
														}}
													/>
													<Toggle
														checked={this.props.gamesEditModal["biases"]["auto"]}
														onChange={(checkbox) => {
															if (checkbox.detail.checked) {
																this.updateParam("biases", { ...this.props.gamesEditModal.biases, auto: checkbox.detail.checked, from: "1970-01-01", to: "9999-12-31" });
															} else {
																this.updateParam("biases", {
																	...this.props.gamesEditModal.biases,
																	auto: checkbox.detail.checked,
																	from: null,
																	to: null,
																});
															}
														}}>
														indefinitely
													</Toggle>

													<SpaceBetween direction="vertical" size="m">
														<Box variant="span">Tags:</Box>
														<Multiselect
															filteringType={this.props.gameTags.filters.search === "" && this.props.gameTags.totalPages === 1 ? "auto" : "manual"}
															filteringPlaceholder="Search tags"
															disabled={this.state.isTagsLoading}
															placeholder="Select tags"
															loadingText="Loading tags..."
															empty="No tags"
															statusType={!this.props.gameTags.loaded && "loading"}
															selectedOptions={this.props.gamesEditModal.tags?.map((tag) => ({ label: `#${tag.id} ${tag.name} (meta Id: ${tag.metaTagId})`, value: String(tag.id) })) || []}
															onChange={({ detail }) => {
																this.props.setGamesEditModal((prev) => ({ ...prev, tags: this.props.gameTags.data.filter((tag) => detail.selectedOptions.map((o) => o.value).indexOf(String(tag.id)) > -1) }));
															}}
															options={this.props.gameTags.data.map((tag) => {
																return { value: String(tag.id), label: `#${tag.id} ${tag.name} (meta Id: ${tag.metaTagId})` };
															})}
															onLoadItems={({ detail }) => {
																if (this.props.gameTags.filters.search === "" && this.props.gameTags.totalPages === 1) return;
																if (typeof detail.filteringText !== "undefined") {
																	this.props.setGameTags((prev) => ({
																		...prev,
																		filters: {
																			...prev.filters,
																			search: detail.filteringText,
																		},
																	}));
																	if (this.searchDebounce) clearTimeout(this.searchDebounce);
																	this.searchDebounce = setTimeout(() => {
																		this.props.setGameTags((prev) => ({
																			...prev,
																			filters: {
																				...prev.filters,
																				page: 1,
																			},
																		}));
																		setTimeout(() => {
																			this.props.Interface.loadGameTags();
																		}, 50);
																	}, 300);
																}
															}}
														/>
													</SpaceBetween>

													<Box variant="span">date from: </Box>

													<RangePicker
														relativeOptions={[]}
														clearButtonLabel="Clear"
														dateOnly={true}
														placeholder="Select range for show stats"
														onChange={(range) => {
															this.updateParam("biases", { ...this.props.gamesEditModal.biases, from: range.detail.value.startDate.length ? range.detail.value.startDate : null, to: range.detail.value.endDate.length ? range.detail.value.endDate : null });
														}}
														value={{ type: "absolute", startDate: this.props.gamesEditModal.biases.from, endDate: this.props.gamesEditModal.biases.to }}
														i18nStrings={{
															customRelativeRangeUnitLabel: "",
															formatRelativeRange: (value) => {
																return value.amount;
															},
															formatUnit: () => {
																return "";
															},
															nextMonthAriaLabel: "Next",
															applyButtonLabel: "Ok",
														}}
													/>
												</SpaceBetween>
											</>
										}
									</>
								) : (
									<Box variant="div" textAlign="center">
										<Spinner size="large" />
									</Box>
								)}
							</SpaceBetween>
						</>
					}
					footer={
						<>
							<Box
								float="right"
								children={
									<>
										<Box
											variant="span"
											margin={{ right: "s" }}
											children={
												<Button
													variant="normal"
													onClick={() => {
														this.props.setGamesEditModal({ ...this.props.gamesEditModal, show: false });
													}}
													children="Close"
												/>
											}
										/>
										<Box
											variant="span"
											children={
												<Button
													variant="primary"
													onClick={() => {
														this.editGame();
													}}
													children="Ok"
												/>
											}
										/>
									</>
								}
							/>
						</>
					}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const gamesEditModal = useRecoilValue(gamesEditModalStore);
		const setGamesEditModal = useSetRecoilState(gamesEditModalStore);
		const game = useRecoilValue(gameStore);
		const setGame = useSetRecoilState(gameStore);
		const config = useRecoilValue(configStore);
		const gameTags = useRecoilValue(gamesTagsStore);
		const setGameTags = useSetRecoilState(gamesTagsStore);

		return <Component {...props} gamesEditModal={gamesEditModal} setGamesEditModal={setGamesEditModal} game={game} setGame={setGame} gameTags={gameTags} setGameTags={setGameTags} config={config} />;
	};
}

export default withRecoil(GamesEditModal);
